import React, { useState, useEffect, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { 
  GroupedTimeEntry, 
  EntryDetail, 
  Project, 
  TeamMember, 
  FilterConfig, 
  GroupedEntryState,
  getDateRange,
  getGroupKey 
} from './config';
import FilterBar from './components/FilterBar';
import EntryGrid from './components/EntryGrid';

const TimesheetApprovals: React.FC = () => {
  // State management
  const [groupedEntries, setGroupedEntries] = useState<GroupedTimeEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Grouped entry state
  const [groupedState, setGroupedState] = useState<GroupedEntryState>({
    expandedGroups: new Set(),
    selectedEntryForEdit: null,
    selectedGroup: null
  });
  
  // Filter state
  const [filters, setFilters] = useState<FilterConfig>(() => {
    const defaultRange = getDateRange(30); // Default to last 30 days
    return {
      projectId: null,
      projectLead: null,
      employeeId: null,
      entryStatus: ['Submitted'],
      dateFrom: defaultRange.from,
      dateTo: defaultRange.to
    };
  });

  // Authentication hooks
  const session = useSession();
  const supabase = useSupabaseClient();

  // Fetch projects for reassignment
  const fetchProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id,
          description,
          job_number,
          project_lead
        `)
        .order('description');

      if (error) throw error;

      const transformedProjects = data.map(project => {
        // Find the project lead from the teamMembers array
        let projectLeadName = 'Unassigned';
        if (project.project_lead && teamMembers.length > 0) {
          const lead = teamMembers.find(member => 
            member.id.toString() === project.project_lead.toString()
          );
          if (lead) {
            projectLeadName = `${lead.first_name} ${lead.last_name}`;
          }
        }

        return {
          id: project.id,
          name: project.description,
          job_number: project.job_number,
          project_lead: project.project_lead,
          project_lead_name: projectLeadName
        };
      });

      setProjects(transformedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    }
  }, [supabase, teamMembers]);

  // Fetch active team members for filtering and project lead resolution
  const fetchTeamMembers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('active_team_members')
        .select('id, first_name, last_name, type')
        .order('first_name');

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members');
    }
  }, [supabase]);

  // Fetch grouped timesheet entries
  const fetchGroupedEntries = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('submitted_timesheet_entries_grouped')
        .select('*')
        .gte('earliest_date', filters.dateFrom)
        .lte('latest_date', filters.dateTo);

      // Apply filters
      if (filters.projectId) {
        query = query.eq('project_id', filters.projectId);
      }
      
      if (filters.projectLead) {
        query = query.eq('project_lead', filters.projectLead);
      }
      
      if (filters.employeeId) {
        query = query.eq('employee_id', filters.employeeId);
      }
      
      if (filters.entryStatus.length > 0) {
        query = query.in('group_status', filters.entryStatus);
      }

      const { data, error } = await query.order('week_ending_date', { ascending: false });

      if (error) throw error;
      setGroupedEntries(data || []);
    } catch (err) {
      console.error('Error fetching grouped entries:', err);
      setError('Failed to load timesheet entries');
    } finally {
      setLoading(false);
    }
  }, [supabase, filters]);

  // Fetch individual entry details for a specific group
  const fetchEntryDetails = useCallback(async (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']): Promise<EntryDetail[]> => {
    try {
      // For leave entries, we need to query differently since there's no project_id
      if (projectId === null && leaveType) {
        const { data, error } = await supabase
          .from('timesheet_entries')
          .select(`
            id,
            project_id,
            date,
            hours,
            description,
            status,
            leave_type
          `)
          .eq('batch_id', batchId)
          .eq('leave_type', leaveType)
          .order('date');

        if (error) throw error;
        
        // Transform to match EntryDetail interface
        const transformedData = (data || []).map(entry => ({
          entry_id: entry.id,
          project_id: entry.project_id,
          date: entry.date,
          hours: entry.hours,
          description: entry.description,
          status: entry.status,
          leave_type: entry.leave_type
        }));

        return transformedData;
      } else if (projectId !== null) {
        const { data, error } = await supabase
          .rpc('get_project_entry_details', {
            p_batch_id: batchId,
            p_project_id: projectId
          });

        if (error) throw error;
        return data || [];
      }
      
      return [];
    } catch (err) {
      console.error('Error fetching entry details:', err);
      throw err;
    }
  }, [supabase]);

  // Initial data fetch
  useEffect(() => {
    if (session) {
      fetchTeamMembers();
    }
  }, [session, fetchTeamMembers]);

  // Fetch projects after team members are loaded
  useEffect(() => {
    if (session && teamMembers.length > 0) {
      fetchProjects();
    }
  }, [session, teamMembers.length, fetchProjects]);

  // Fetch entries when filters change
  useEffect(() => {
    if (session) {
      fetchGroupedEntries();
    }
  }, [session, fetchGroupedEntries]);

  // Handle group allocation/approval - allocate project entries or approve leave entries
  const handleGroupAllocate = useCallback(async (batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => {
    try {
      console.log('handleGroupAllocate called with:', { batchId, projectId, leaveType });
      
      if (projectId === null && leaveType) {
        // For leave entries, approve all entries of this leave type in the batch
        console.log('Approving leave entries:', { batchId, leaveType });
        
        const { data, error } = await supabase
          .from('timesheet_entries')
          .update({ status: 'Approved' })
          .eq('batch_id', batchId)
          .eq('leave_type', leaveType)
          .eq('status', 'Submitted') // Only approve submitted entries
          .select();

        if (error) {
          console.error('Database error:', error);
          throw error;
        }
        
        console.log('Leave entries updated:', data);
      } else if (projectId !== null) {
        // For project entries, use the existing RPC function
        console.log('Allocating project entries:', { batchId, projectId });
        
        const { error } = await supabase
          .rpc('allocate_project_entries', {
            p_batch_id: batchId,
            p_project_id: projectId
          });

        if (error) {
          console.error('RPC error:', error);
          throw error;
        }
      } else {
        console.error('Invalid parameters:', { batchId, projectId, leaveType });
        setError('Invalid entry type for allocation/approval');
        return;
      }

      // Refresh grouped entries to reflect updated statuses
      console.log('Refreshing grouped entries...');
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error processing group:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to ${leaveType ? 'approve' : 'allocate'} group: ${errorMessage}`);
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle group hide (keeping for interface compatibility, but not used in UI)
  const handleGroupHide = useCallback(async (batchId: number, projectId: number | null) => {
    try {
      // For leave entries, we need a different approach since there's no project_id
      if (projectId === null) {
        setError('Leave entries cannot be bulk hidden - use individual entry actions');
        return;
      }

      const { error } = await supabase
        .rpc('hide_project_entries', {
          p_batch_id: batchId,
          p_project_id: projectId
        });

      if (error) throw error;

      // Refresh grouped entries to reflect updated statuses
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error hiding group:', err);
      setError('Failed to hide group');
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle individual entry allocation/approval
  const handleEntryAllocate = useCallback(async (entryId: number) => {
    try {
      // First, check if this is a leave entry
      const { data: entryData, error: fetchError } = await supabase
        .from('timesheet_entries')
        .select('leave_type')
        .eq('id', entryId)
        .single();

      if (fetchError) throw fetchError;

      // Set status based on entry type
      const newStatus = entryData.leave_type ? 'Approved' : 'Allocated';
      
      const { error } = await supabase
        .from('timesheet_entries')
        .update({ status: newStatus })
        .eq('id', entryId);

      if (error) throw error;

      // Refresh grouped entries to update status
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error updating entry status:', err);
      setError('Failed to update entry status');
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle individual entry hide
  const handleEntryHide = useCallback(async (entryId: number) => {
    try {
      const { error } = await supabase
        .from('timesheet_entries')
        .update({ status: 'Hidden' })
        .eq('id', entryId);

      if (error) throw error;

      // Refresh grouped entries to update status
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error hiding entry:', err);
      setError('Failed to hide entry');
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle individual entry update
  const handleEntryUpdate = useCallback(async (entryId: number, projectId: number | null, hours: number, isLeaveEntry?: boolean) => {
    try {
      // For leave entries, only allow hours updates, not project changes
      if (isLeaveEntry) {
        const { error } = await supabase
          .from('timesheet_entries')
          .update({ hours: hours })
          .eq('id', entryId);

        if (error) throw error;
      } else {
        // For project entries, allow both project and hours updates
        const { error } = await supabase
          .from('timesheet_entries')
          .update({
            project_id: projectId,
            hours: hours
          })
          .eq('id', entryId);

        if (error) throw error;
      }

      // Refresh grouped entries to reflect changes
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error updating entry:', err);
      setError('Failed to update entry');
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle individual entry undo hide (set back to Submitted)
  const handleEntryUndoHide = useCallback(async (entryId: number) => {
    try {
      const { error } = await supabase
        .from('timesheet_entries')
        .update({ status: 'Submitted' })
        .eq('id', entryId);

      if (error) throw error;

      // Refresh grouped entries to update status
      await fetchGroupedEntries();
    } catch (err) {
      console.error('Error undoing hide:', err);
      setError('Failed to undo hide');
    }
  }, [supabase, fetchGroupedEntries]);

  // Handle group expansion toggle
  const handleGroupToggle = useCallback((batchId: number, projectId: number | null, leaveType?: GroupedTimeEntry['leave_type']) => {
    const groupKey = getGroupKey(batchId, projectId, leaveType);
    setGroupedState(prev => {
      const newExpandedGroups = new Set(prev.expandedGroups);
      if (newExpandedGroups.has(groupKey)) {
        newExpandedGroups.delete(groupKey);
      } else {
        newExpandedGroups.add(groupKey);
      }
      return {
        ...prev,
        expandedGroups: newExpandedGroups
      };
    });
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<FilterConfig>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  if (loading && groupedEntries.length === 0) {
    return (
      <div className="timesheet-approvals-loading">
        <div>Loading timesheet entries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timesheet-approvals-error">
        <h3>Error loading timesheet approvals</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  return (
    <div className="timesheet-approvals">
      <FilterBar
        filters={filters}
        projects={projects}
        teamMembers={teamMembers}
        onFilterChange={handleFilterChange}
      />

      <div className="timesheet-approvals-content">
        <EntryGrid
          groupedEntries={groupedEntries}
          groupedState={groupedState}
          projects={projects}
          onGroupAllocate={handleGroupAllocate}
          onGroupHide={handleGroupHide}
          onEntryAllocate={handleEntryAllocate}
          onEntryHide={handleEntryHide}
          onEntryUpdate={handleEntryUpdate}
          onEntryUndoHide={handleEntryUndoHide}
          onGroupToggle={handleGroupToggle}
          onFetchEntryDetails={fetchEntryDetails}
        />
      </div>
    </div>
  );
};

export default TimesheetApprovals; 